import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, filter, map, take, tap } from 'rxjs/operators';
import Env from '../../env';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { AuthService } from './auth.service';
import { DeviceIdService } from './device-id.service';
import { Temporal } from '@js-temporal/polyfill';
import { AppStartupService } from './app-startup.service';

type Translation = {
    lang: string;
    translation: string;
};

type Translations = {
    translations: Record<string, string>;
};

export type Ticket = {
    id: string;
    docNum: number;
    categoryCode: string;
    cardId: string;
    cardType: string;
    units: number;
    initialUnits: number;
    timestamp: string;
    price: number;
    productTranslations: Translation[];
    productName: string;
    email: string;
    transportCustomerId: number | string;
    customerAccountId: number | string;
    purchaseId: number;
    productId: string;
    productCode: string;
    code: string;
    descriptions: Translations;
    names: Translations;
    farePrice: {
        amount: number;
        currency: string;
    };
    status: string;
    type: string;
    typeOfProduct: {
        code: string;
        name: Translations;
        description: Translations;
    };
    mtb: string;
    pdfToken: string;
    loaned: boolean;
    lendable: boolean;
    transferable: boolean;
    isOwner: boolean;
    activated: boolean;
    validated: boolean;
    repurchasable: boolean;
    purchaseType: string;
    bookletEnding: string;
    expiredAt: string;
    startZoneId: number;
    startZoneName: string;
    endZoneId: number;
    endZoneName: string;
    ending?: string;
};

type TicketsState = {
    loaded: boolean;
    deviceTickets: Ticket[];
    allDeviceTicketsLoaded: boolean;
    userTickets: Ticket[];
    allUserTicketsLoaded: boolean;
};

@Injectable({
    providedIn: 'root'
})
export class TicketsService {
    public TIMEZONE = this.appStartupService.getTimezone();
    // State
    public PAGE_SIZE = 10;
    private INITIAL_STATE: TicketsState = {
        loaded: false,
        deviceTickets: [],
        allDeviceTicketsLoaded: false,
        userTickets: [],
        allUserTicketsLoaded: false
    };
    private state$ = new BehaviorSubject<TicketsState>(this.INITIAL_STATE);
    private reloadEvent$ = new Subject<void>();
    private nextPageEvent$ = new Subject<void>();

    // Selectors
    public loaded$ = this.state$.pipe(map((state) => state.loaded));
    public tickets$ = this.state$.pipe(map((state) => state.deviceTickets.concat(state.userTickets)));
    public deviceTickets$ = this.state$.pipe(map((state) => state.deviceTickets));
    public userTickets$ = this.state$.pipe(map((state) => state.userTickets));
    public activeTickets$ = this.tickets$.pipe(
        map((tickets) =>
            tickets.filter((t) => t.activated && !this.isExpiredMultiUnitWithRemainingUnits(t)).sort(this.sortTickets)
        )
    );

    public inactiveTickets$ = this.tickets$.pipe(
        map((tickets) =>
            tickets
                .filter(
                    (t) =>
                        this.isExpiredMultiUnitWithRemainingUnits(t) || (!t.activated && !this.isTicketRecentlyUsed(t))
                )
                .sort(this.sortTickets)
        )
    );

    public recentlyUsedTickets$ = this.tickets$.pipe(
        map((tickets) =>
            tickets.filter((t) => !t.activated && t.validated && this.isTicketRecentlyUsed(t)).sort(this.sortTickets)
        )
    );

    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private auth: AuthService,
        private device: DeviceIdService,
        private appStartupService: AppStartupService
    ) {}

    // Actions
    public reloadTickets() {
        this.reloadEvent$.next();
    }

    public loadNextPageTickets() {
        this.nextPageEvent$.next();
    }

    // Effects
    public registerReload() {
        return this.reloadEvent$.pipe(
            exhaustMap(() => {
                this.state$.next(this.INITIAL_STATE);
                return forkJoin([this.getDeviceTickets(0), this.getUserTickets(0)]).pipe(
                    tap(([deviceTickets, userTickets]) => {
                        const allDeviceTicketsLoaded = deviceTickets.length < this.PAGE_SIZE;
                        const allUserTicketsLoaded = userTickets.length < this.PAGE_SIZE;
                        this.state$.next({
                            loaded: true,
                            deviceTickets,
                            allDeviceTicketsLoaded,
                            userTickets,
                            allUserTicketsLoaded
                        });
                    })
                );
            })
        );
    }

    public registerNextPage() {
        return this.nextPageEvent$.pipe(
            exhaustMap(() =>
                this.state$.pipe(
                    take(1),
                    filter((state) => {
                        return !state.allDeviceTicketsLoaded || !state.allUserTicketsLoaded;
                    }),
                    exhaustMap((state) => {
                        this.state$.next({ ...state, loaded: false });
                        let deviceTickets$ = of(<Ticket[]>[]);
                        if (!state.allDeviceTicketsLoaded) {
                            const devicePage = Math.floor(state.deviceTickets.length / this.PAGE_SIZE);
                            deviceTickets$ = this.getDeviceTickets(devicePage);
                        }
                        let userTickets$ = of(<Ticket[]>[]);
                        if (!state.allUserTicketsLoaded) {
                            const userPage = Math.floor(state.userTickets.length / this.PAGE_SIZE);
                            userTickets$ = this.getUserTickets(userPage);
                        }
                        return forkJoin([deviceTickets$, userTickets$]).pipe(
                            tap(([dt, ut]) => {
                                const deviceTickets = this.mergeUniqueTickets(state.deviceTickets, dt);
                                const userTickets = this.mergeUniqueTickets(state.userTickets, ut);
                                const allDeviceTicketsLoaded = dt.length < this.PAGE_SIZE;
                                const allUserTicketsLoaded = ut.length < this.PAGE_SIZE;
                                this.state$.next({
                                    loaded: true,
                                    deviceTickets,
                                    allDeviceTicketsLoaded,
                                    userTickets,
                                    allUserTicketsLoaded
                                });
                            })
                        );
                    })
                )
            )
        );
    }

    public resetState(markLoaded = false) {
        this.state$.next({
            ...this.INITIAL_STATE,
            loaded: markLoaded
        });
    }

    private mergeUniqueTickets(a: Ticket[], b: Ticket[]): Ticket[] {
        const ticketMap = new Map(
            a.concat(b).map((ticket) => {
                if (ticket.code) {
                    return [ticket.code, ticket];
                }
                if (ticket.id) {
                    return [ticket.id, ticket];
                }
                return [ticket.docNum.toString(), ticket];
            })
        );
        return Array.from(ticketMap.values());
    }

    private sortTickets(a: Ticket, b: Ticket): number {
        if (a.timestamp && b.timestamp) {
            return Temporal.Instant.from(b.timestamp).epochSeconds - Temporal.Instant.from(a.timestamp).epochSeconds;
        }
        if (a.code && b.code) {
            return Number(b.code) - Number(a.code);
        }
        return b.docNum - a.docNum;
    }

    private getDeviceTickets(page: number): Observable<Ticket[]> {
        if (!this.env.DEVICE_CUSTOMER_ENABLED || !Capacitor.isNativePlatform()) {
            return of([]);
        }
        const token = this.device.getAccessDeviceToken();
        return this.getTickets(token, page, { deviceTokenOnly: true });
    }

    private getUserTickets(page: number): Observable<Ticket[]> {
        if (!this.auth.getLoginStatus()) {
            return of([]);
        }
        const token = this.auth.getLocalAccessToken();
        return this.getTickets(token, page);
    }

    private getTickets(
        token: string,
        page: number,
        extraParams: Record<string, string | number | boolean> = {}
    ): Observable<Ticket[]> {
        let url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/ticket/active`;

        if (this.env.WMB?.WMB_PUBLIC_API) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/ticket`;
        }

        const expiredQrTicketsAvailable = this.env.EXPIRED_QR_TICKETS_AVAILABLE
            ? Temporal.Duration.from(this.env.EXPIRED_QR_TICKETS_AVAILABLE)
            : null;

        const params = {
            channel: this.getChannel(),
            limit: this.PAGE_SIZE,
            page,
            sort: 'desc',
            ...(expiredQrTicketsAvailable && {
                expiredHours: expiredQrTicketsAvailable.hours,
                expiredMinutes: expiredQrTicketsAvailable.minutes
            }),
            ...extraParams
        };

        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`
        });

        return this.http.get<Ticket[]>(url, { params, headers }).pipe(catchError(() => of([])));
    }

    private getChannel(): string {
        return this.env?.TICKETS_CHANNEL ?? this.env.CHANNEL;
    }

    private isTicketRecentlyUsed(ticket: Ticket): boolean {
        const ticketEndingIso = ticket?.ending;
        if (!ticketEndingIso) return false;
        const now = Temporal.Now.zonedDateTimeISO(this.TIMEZONE).toInstant();
        const hasOffset = ticketEndingIso.includes('+') || ticketEndingIso.includes('Z');
        const ticketEnd = hasOffset
            ? Temporal.Instant.from(ticketEndingIso)
            : Temporal.ZonedDateTime.from(`${ticketEndingIso}[${this.TIMEZONE}]`).toInstant();
        return Temporal.Instant.compare(now, ticketEnd) > 0;
    }

    /**
     * Checks if a ticket's ending time has passed.
     * Used for determining if multi-unit tickets should be moved to inactive.
     */
    private hasEndingPassed(ticket: Ticket): boolean {
        return this.isTicketRecentlyUsed(ticket);
    }

    /**
     * Checks if a multi-unit ticket has remaining units.
     * For M/H type tickets bought multiple at a time.
     */
    private hasRemainingUnits(ticket: Ticket): boolean {
        return ticket.initialUnits != null && ticket.initialUnits > 1 && ticket.units != null && ticket.units > 0;
    }

    /**
     * Checks if a ticket is an expired multi-unit ticket with remaining units.
     * These tickets should be shown in inactive list (can be reused).
     */
    private isExpiredMultiUnitWithRemainingUnits(ticket: Ticket): boolean {
        return this.hasEndingPassed(ticket) && this.hasRemainingUnits(ticket);
    }
}
